import { chain } from 'lodash';
import React, { ReactElement, useMemo } from 'react';
import { DomainTypeGq } from '../../../graphql/projects/models/DomainTypeGq';
import { ProjectGq } from '../../../graphql/projects/models/ProjectGq';
import VoxelAutocomplete, {
  GenericOption,
  VoxelAutocompleteProps,
} from '../../voxel/Forms/VoxelAutocomplete';

export interface VoxelAutocompleteDomainMultiProps
  extends Omit<
    VoxelAutocompleteProps,
    'setSelectedOptions' | 'options' | 'selectedOptionValues' | 'fixedOptions' | 'freeAdd' | 'multi'
  > {
  project: ProjectGq;
  domainIds?: number[];
  setDomainIds?: (domainIds: number[]) => void;
  bottomElements?: ReactElement[];
}

export const VoxelAutocompleteDomainMulti: React.FC<VoxelAutocompleteDomainMultiProps> = ({
  project,
  setDomainIds,
  domainIds,
  label,
  labelPosition,
  placeholder,
  bottomElements,
  ...forwardedProps
}) => {

  const manualDomains = useMemo(() => project.domains.filter((d) => d.type !== DomainTypeGq.AUTOMATIC), [project.domains]);
  const automaticDomains = useMemo(() => project.domains
    .filter((d) => project.useAutomaticDomains ? d.type === DomainTypeGq.AUTOMATIC : false)
    .filter(d => !manualDomains.some(m => m.protocol + m.hostName === d.protocol + d.hostName))
    ,
    [project.domains, project.useAutomaticDomains]);

  const domainsList = useMemo(() => [...manualDomains, ...automaticDomains], [manualDomains, automaticDomains]);

  const options = useMemo(() => {
    return chain(domainsList)
      .map((d): GenericOption => ({ title: d.name, value: d.id }))
      .orderBy([(t) => t.title, (t) => t.value])
      .value();
  }, [domainsList]);

  return (
    <VoxelAutocomplete
      {...forwardedProps}
      options={options}
      multi
      selectedOptionValues={domainIds}
      setSelectedOptions={(options) => setDomainIds(options.map((opt) => opt.value))}
      label={label ?? 'Domains'}
      placeholder={placeholder}
      labelPosition={labelPosition ?? 'top'}
      bottomElements={
        bottomElements
      }
    />
  );
};

export default VoxelAutocompleteDomainMulti;
